import {
  Button,
  DataLossPrompt,
  DateTimeInput,
  Footer,
  NumberInput,
  Panel,
  SelectInput,
  SwitchInput,
  TextInput,
  Typography,
} from 'melp-design/components';
import { Box, Collapse, Divider, Stack } from '@mui/material';
import moment from 'moment';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMe } from 'state/Administrators';
import { usePredefinedToasts } from 'utils/Toast';
import { useRouteParams } from 'utils/useIdParam';
import {
  ALLOCATION_PRIORITY_BY_LEVEL,
  benefitCategories,
  BenefitCategory,
  BenefitStatus,
  useBenefit,
  useUpdateBenefit,
} from 'store/benefits';
import { ClaimsDisabledFeatureAlert } from 'containers/claims';
import { FrequencyUnit, frequencyUnits } from 'types/general';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { ROUTES } from 'config';
import { formatCurrency, formatPercents } from 'utils/format';
import { fromCents, toCents } from 'utils/general';
import { useConfirm } from 'store/confirm';
import { Level } from 'types/Common';

interface Values {
  name: string;
  type: BenefitCategory;
  status: BenefitStatus;
  value: number;
  investmentAmount: number;
  allowClaimReimbursement: boolean;
  employeeContribution: number;
  employeeContributionRequired: boolean;
  investmentPeriod: FrequencyUnit;
  isVisibleForEmployees: boolean;
  isBalanceTrackingEnabled: boolean;
  isPreview: boolean;
  internalComment: string;
  previewDate: string | null;
  publishedAt: string | null;
  activatedAt: string | null;
  deactivatedAt: string | null;
  isArchived: boolean;
  allocationPriority: number;
  taxExemptionCountry: string | null;
}

const INITIAL_VALUES: Values = {
  name: '',
  type: benefitCategories[0],
  status: 'draft',
  value: NaN,
  investmentAmount: NaN,
  allowClaimReimbursement: false,
  employeeContribution: NaN,
  employeeContributionRequired: false,
  investmentPeriod: 'once',
  isVisibleForEmployees: false,
  isBalanceTrackingEnabled: false,
  isPreview: false,
  internalComment: '',
  previewDate: null,
  publishedAt: null,
  activatedAt: null,
  deactivatedAt: null,
  isArchived: false,
  allocationPriority: ALLOCATION_PRIORITY_BY_LEVEL['medium'],
  taxExemptionCountry: null,
};

interface Props {
  onSaveSuccess: () => void;
  guided: boolean;
}

export const BenefitSettings: FC<Props> = ({ onSaveSuccess, guided }) => {
  const { t, i18n } = useTranslation();
  const { id: benefitId } = useRouteParams(ROUTES.benefits.details);
  const predefinedToasts = usePredefinedToasts();
  const {
    me,
    canEditBenefits,
    isFeatureEnabled,
    taxExemptionCountries,
    hasEditPermission,
  } = useMe();
  const canEdit = canEditBenefits && hasEditPermission(['benefits']);
  const { confirmation } = useConfirm();

  const currency = me?.parentCompany?.defaultCurrency;

  const { data: benefit } = useBenefit(benefitId);

  const {
    control,
    handleSubmit,
    getFieldState,
    reset,
    watch,
    formState: { dirtyFields, isSubmitted, errors },
  } = useForm({
    defaultValues: INITIAL_VALUES,
  });

  useEffect(() => {
    if (benefit) {
      reset({
        employeeContributionRequired: !!benefit.employeeContribution,
        name: benefit.name,
        type: benefit.type,
        status: benefit.status,
        value: fromCents(benefit.value),
        investmentAmount: fromCents(benefit.investmentAmount),
        allowClaimReimbursement: benefit.allowClaimReimbursement,
        employeeContribution: fromCents(benefit.employeeContribution),
        investmentPeriod: benefit.investmentPeriod,
        isVisibleForEmployees: benefit.isVisibleForEmployees,
        isBalanceTrackingEnabled: benefit.isBalanceTrackingEnabled,
        isPreview: benefit.isPreview,
        internalComment: benefit.internalComment,
        previewDate: benefit.previewDate,
        publishedAt: benefit.publishedAt,
        activatedAt: benefit.activatedAt,
        deactivatedAt: benefit.deactivatedAt,
        isArchived: benefit.isArchived,
        allocationPriority: benefit.allocationPriority,
        taxExemptionCountry: benefit.taxExemptionCountry,
      });
    }
  }, [benefit, reset]);

  const { mutate: updateBenefit, isLoading: isUpdating } = useUpdateBenefit();

  const isDirty = !!Object.keys(dirtyFields).length && !isSubmitted;
  const [
    status,
    investmentAmount,
    investmentPeriod,
    benefitValue,
    employeeContribution,
    employeeContributionRequired,
    allowClaimReimbursement,
    activatedAt,
    deactivatedAt,
  ] = watch([
    'status',
    'investmentAmount',
    'investmentPeriod',
    'value',
    'employeeContribution',
    'employeeContributionRequired',
    'allowClaimReimbursement',
    'activatedAt',
    'deactivatedAt',
  ]);

  const onSubmit: SubmitHandler<Values> = async ({
    employeeContributionRequired,
    isPreview,
    value,
    investmentAmount,
    employeeContribution,
    ...values
  }) => {
    if (
      benefit?.status !== 'active' &&
      benefit?.publishStatus !== 'published' &&
      values.activatedAt &&
      values.publishedAt &&
      moment(values.activatedAt).isBefore(values.publishedAt)
    ) {
      const { confirmed } = await confirmation(
        t('benefits.publishedLaterThanActiveWarning'),
      );

      if (!confirmed) return;
    }

    updateBenefit(
      {
        benefitId,
        data: {
          ...values,
          previewDate:
            getFieldState('isPreview').isTouched && !!isPreview
              ? moment().toISOString(true)
              : benefit?.previewDate ?? null,
          internalComment: benefit?.internalComment ?? '',
          isDraft: benefit?.status === 'draft',
          value: toCents(value),
          investmentAmount: toCents(investmentAmount),
          employeeContribution: employeeContributionRequired
            ? toCents(employeeContribution)
            : NaN,
        },
      },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
          onSaveSuccess();
        },
      },
    );
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={2}>
          <Panel title={t('benefits.benefitGeneralSettingsSection')}>
            <Stack gap={2}>
              <Controller
                name="name"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <TextInput
                      label={t('common.systemName')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      required
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />

              <Controller
                name="type"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <SelectInput
                    label={t('marketplaceItems.benefitPlanTypes')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    options={benefitCategories.map((category) => ({
                      value: category,
                      label: t(`menu.${category}`),
                    }))}
                    disabled={!canEdit}
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="isPreview"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SwitchInput
                      label={t('benefits.isPreview.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />

              <Controller
                name="isArchived"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SwitchInput
                      label={t('benefits.isArchived.label')}
                      info={t('benefits.isArchived.info')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />
            </Stack>
          </Panel>

          <Panel
            title={t('benefits.benefitFinanceSection')}
            subtitle={t('benefits.benefitFinanceSectionHint')}
            collapsible
            isInitialExpanded={!guided}
          >
            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                <Box flex={0.5}>
                  <Controller
                    name="investmentAmount"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => (
                      <NumberInput
                        label={t('common.investment')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        disabled={!canEdit}
                        key={value}
                        ref={ref}
                        sx={{ width: '100%' }}
                        type="integer"
                        InputProps={{ endAdornment: currency }}
                      />
                    )}
                  />
                </Box>

                <Box flex={0.5}>
                  <Controller
                    name="investmentPeriod"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => {
                      return (
                        <SelectInput
                          name={name}
                          value={value}
                          onChange={onChange}
                          options={frequencyUnits
                            .filter((period) =>
                              ['once', 'monthly'].includes(period),
                            )
                            .map((period) => ({
                              label: t(
                                `recognition.allowanceRuleFrequency-${period}`,
                              ),
                              value: period,
                            }))}
                          disabled={!canEdit}
                          ref={ref}
                        />
                      );
                    }}
                  />
                </Box>
              </Stack>

              <Controller
                name="isVisibleForEmployees"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SwitchInput
                      label={t('benefits.show_on_app')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />

              <Controller
                name="employeeContributionRequired"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SwitchInput
                      label={t('benefits.employeeContribution')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />

              <Collapse in={employeeContributionRequired}>
                <Controller
                  name="employeeContribution"
                  control={control}
                  rules={{
                    validate: (v) =>
                      v > investmentAmount
                        ? t(
                            'benefits.employeeContributionCannotExceedInvestment',
                          )
                        : undefined,
                  }}
                  render={({ field: { name, value, onChange, ref } }) => (
                    <NumberInput
                      label={t('common.investment')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!investmentAmount || !canEdit}
                      errorMessage={errors.employeeContribution?.message}
                      InputProps={{ endAdornment: currency }}
                      sx={{ width: '50%' }}
                      type="integer"
                      key={value}
                      ref={ref}
                    />
                  )}
                />

                <Collapse
                  in={
                    !!employeeContribution &&
                    !!investmentAmount &&
                    employeeContribution <= investmentAmount
                  }
                >
                  <Stack gap={1} p={1}>
                    <Typography variant="p2" color="textSecondary">{`${t(
                      'benefits.employerInvests',
                    )}: ${formatCurrency(
                      i18n.language,
                      toCents(investmentAmount - employeeContribution),
                      currency,
                    )} (${formatPercents(
                      i18n.language,
                      1 - employeeContribution / investmentAmount,
                    )})`}</Typography>
                    <Typography variant="p2" color="textSecondary">{`${t(
                      'benefits.employeeInvests',
                    )}: ${formatCurrency(
                      i18n.language,
                      toCents(employeeContribution),
                      currency,
                    )} (${formatPercents(
                      i18n.language,
                      employeeContribution / investmentAmount,
                    )})`}</Typography>
                  </Stack>
                </Collapse>
              </Collapse>

              <Controller
                name="value"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => (
                  <NumberInput
                    label={`${t('benefits.value')} (${t(
                      `recognition.allowanceRuleFrequency-${investmentPeriod}`,
                    )})`}
                    name={name}
                    value={value}
                    onChange={onChange}
                    InputProps={{ endAdornment: currency }}
                    sx={{ width: '50%' }}
                    disabled={!canEdit}
                    key={value}
                    type="integer"
                    ref={ref}
                  />
                )}
              />

              <Controller
                name="isBalanceTrackingEnabled"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SwitchInput
                      label={t('benefits.trackBalance')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />

              <Controller
                name="allowClaimReimbursement"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SwitchInput
                      label={t('benefits.allowClaimReimbursement.label')}
                      disabled={
                        !isFeatureEnabled('claimReimbursement') || !canEdit
                      }
                      name={name}
                      value={value}
                      onChange={onChange}
                      ref={ref}
                    />
                  );
                }}
              />
              <ClaimsDisabledFeatureAlert />

              {taxExemptionCountries.length ? (
                <Collapse in={allowClaimReimbursement}>
                  <Controller
                    name="taxExemptionCountry"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => {
                      return (
                        <>
                          {taxExemptionCountries.map((country) => (
                            <SwitchInput
                              label={t(
                                `benefits.taxExemptionCountry.${country}.label`,
                              )}
                              name={name}
                              value={value === country}
                              onChange={(checked) => {
                                onChange(checked ? country : null);
                              }}
                              disabled={
                                !canEdit ||
                                !isFeatureEnabled(`taxExemption${country}`)
                              }
                              ref={ref}
                              key={country}
                            />
                          ))}
                        </>
                      );
                    }}
                  />
                </Collapse>
              ) : null}

              <Controller
                name="allocationPriority"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SelectInput
                      label={t('benefits.allocationPriority.label')}
                      infoMessage={t('benefits.allocationPriority.info')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      options={Object.entries(ALLOCATION_PRIORITY_BY_LEVEL).map(
                        ([key, value]) => ({
                          label: t(`common.level.${key as Level}`),
                          value,
                        }),
                      )}
                      disabled={!canEdit}
                      ref={ref}
                    />
                  );
                }}
              />
            </Stack>
          </Panel>

          <Panel title={t('benefits.benefitDatesSection')}>
            <Stack gap={2}>
              <Stack direction="row" spacing={1}>
                <Controller
                  name="activatedAt"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <DateTimeInput
                        label={t('benefits.activationDate')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        required={
                          !Number.isNaN(benefitValue) ||
                          !Number.isNaN(investmentAmount)
                        }
                        disabled={!canEdit}
                        datePickerProps={{
                          maxDateTime: moment(deactivatedAt),
                        }}
                        ref={ref}
                      />
                    );
                  }}
                />

                <Divider orientation="vertical" variant="middle" flexItem />

                <Controller
                  name="deactivatedAt"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <DateTimeInput
                        label={t('benefits.deactivationDate')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        required={
                          !Number.isNaN(benefitValue) ||
                          !Number.isNaN(investmentAmount)
                        }
                        disabled={!canEdit}
                        datePickerProps={{
                          minDateTime: moment(activatedAt),
                        }}
                        ref={ref}
                      />
                    );
                  }}
                />
              </Stack>

              {status === 'draft' ? null : (
                <Box sx={{ width: '50%' }}>
                  <Controller
                    name="publishedAt"
                    control={control}
                    rules={{
                      validate: (v) =>
                        v && deactivatedAt && moment(v).isAfter(deactivatedAt)
                          ? t('benefits.publishDateAfterDeactivationDate')
                          : undefined,
                    }}
                    render={({ field: { name, value, onChange, ref } }) => {
                      return (
                        <DateTimeInput
                          label={t('benefits.publishDate')}
                          name={name}
                          value={value}
                          onChange={onChange}
                          required={benefit?.status !== 'draft'}
                          disabled={!canEdit}
                          error={errors.publishedAt?.message}
                          datePickerProps={{
                            maxDateTime: deactivatedAt
                              ? moment(deactivatedAt)
                              : undefined,
                          }}
                          ref={ref}
                        />
                      );
                    }}
                  />
                </Box>
              )}
            </Stack>
          </Panel>
        </Stack>

        <Footer visible={isDirty || guided}>
          <Stack direction="row" justifyContent="end" gap={1}>
            <Button
              label={t('common.cancel')}
              variant="neutral-outline"
              onClick={() => {
                reset(undefined, { keepDirty: false });
              }}
            />
            <Button
              label={t('common.save')}
              variant="primary"
              type="submit"
              disabled={
                isUpdating || !canEdit || !hasEditPermission(['benefits'])
              }
            />
          </Stack>
        </Footer>
      </form>

      <DataLossPrompt when={isDirty && hasEditPermission(['benefits'])} />
    </>
  );
};
